// Last-attempt VASP failure, shared by every VAISP surface — investor and
// property-owner registrations (`vasp_users`), properties (`vasp_properties`),
// offerings (`vasp_offerings`) and orders (`vasp_orders`).
//
// admin-service records the outcome of every admin-initiated Ctrl-Alt call
// (approve / reject / sync / send / submit / sweep) so the reason outlives the
// request that produced it. Reads never write, and inbound webhooks keep their
// own audit trail in `vasp_webhook_events` — so a `lastError` here always
// describes an action an operator took.
//
// Distinct from the other error fields these drawers show:
//   - `vaspRejectionReason` — the admin's own note, typed into the reject dialog
//   - `verification.error`  — the live DLD verification's own error, not persisted
// This one is the upstream failure of the last write attempt.
import { formatDateTime } from '@shared/utils'
import { VASP_USER_STATUS } from './vaspStatus'

// Which hop failed. Mirrors admin-service's VASP_FAILURE_SOURCE, which it
// derives at the throw site (its own guard vs the gateway vs the vendor).
export const VASP_FAILURE_SOURCE = {
  ADMIN_SERVICE: 'admin_service',
  VAISP_GATEWAY: 'vaisp_gateway',
  CTRL_ALT: 'ctrl_alt',
} as const

export type VaspFailureSource = (typeof VASP_FAILURE_SOURCE)[keyof typeof VASP_FAILURE_SOURCE]

// Banner title per source — OCP lookup (coding-principles §9.4) keyed by the raw
// wire string, so a new or renamed upstream resolves to the generic fallback
// instead of rendering a blank heading. Adding a fourth hop is one row.
export const VASP_FAILURE_TITLE: Record<string, string> = {
  [VASP_FAILURE_SOURCE.ADMIN_SERVICE]: 'Rejected before it reached Ctrl-Alt',
  [VASP_FAILURE_SOURCE.VAISP_GATEWAY]: 'Could not reach Ctrl-Alt',
  [VASP_FAILURE_SOURCE.CTRL_ALT]: 'Ctrl-Alt reported an error',
}

// Used when the source is absent (older rows) or carries a value we have no
// copy for. Deliberately not `formatKey(source)` — an operator does not know
// the service topology, and the sentence below the title still carries the
// actionable content. Kept call-agnostic because this serves registrations,
// listings and orders alike.
const FALLBACK_TITLE = 'The last VAISP attempt failed'

// Shown for a failure we only know about from the mutation that just threw:
// the error body carries no source discriminator, so naming one would be a guess.
export const TRANSIENT_FAILURE_TITLE = 'Last attempt failed'

// admin-service prefixes the source into `last_error` because the DB row is the
// operator's last resort. In the drawer the title already says it, so strip the
// prefix rather than reading "Ctrl-Alt reported an error / Ctrl-Alt: ...".
const SOURCE_PREFIXES: string[] = ['Ctrl-Alt:', 'VASP gateway:', 'Validation:']

// The last attempt succeeded, so there is nothing to report — a stale
// `lastError` on an active registration is history, not a current problem.
const SUCCEEDED_STATUSES: string[] = [VASP_USER_STATUS.ACTIVE, VASP_USER_STATUS.APPROVED]

// Same rule on the listing axis, which offerings run on. Values mirror
// VASP_LISTING_STATUS in modules/offerings/constants/offeringStatus.ts — declared
// rather than imported because `shared/` may not depend on a module
// (architecture-principles §11), the same reason vaspStatus.ts declares its own.
// A listing status is NOT a VaspUserStatus: 'open' has no equivalent there, so
// routing one through `vaspUserStatus` would silently fail to suppress.
const LISTING_SUCCEEDED_STATUSES: string[] = ['open', 'filled', 'approved', 'completed']

// Structural input: both modules' VASP detail view models satisfy it by field
// name, so each drawer body calls `vaspFailureView(props.details)` with no adapter.
export interface VaspFailureInput {
  lastError?: string | null
  lastErrorSource?: string | null
  retryCount?: number | null
  lastSyncedAt?: string | null
  /**
   * Pre-existing field. Used as the timestamp fallback so the meta line works
   * before `lastSyncedAt` ships, and for rows that predate it.
   */
  lastAttemptAt?: string | null
  /** Registration axis (investors, property owners). */
  vaspUserStatus?: string | null
  /** Listing axis (offerings). Orders pass neither — see `vaspFailureView`. */
  vaspListingStatus?: string | null
}

export interface VaspFailureView {
  title: string
  message: string
  /** "Attempt 3 · 18 Aug 2026 · 06:04 PM" — empty when neither part is known. */
  meta: string
}

const stripSourcePrefix = (sentence: string): string => {
  const prefix = SOURCE_PREFIXES.find((p) => sentence.startsWith(p))
  return prefix ? sentence.slice(prefix.length).trim() : sentence
}

const buildMeta = (retryCount: number | null | undefined, at: string): string => {
  const parts: string[] = []
  if (retryCount && retryCount > 0) parts.push(`Attempt ${retryCount}`)
  if (at) parts.push(formatDateTime(at))
  return parts.join(' · ')
}

// admin-service's withVaspAttempt writes `retry_count: 0` on a success and
// `previous + 1` on a failure, so a count at or above this alongside a
// `lastError` is positive evidence that the *last* write failed.
const RECORDED_A_FAILED_ATTEMPT = 1

// True when the entity's own status says the last attempt already succeeded, on
// whichever axis the caller runs on. Orders pass neither status and rely on
// `withVaspAttempt` clearing `last_error` on success server-side — adding an
// order-shaped branch here would not earn its keep.
const hasSucceeded = (input: VaspFailureInput): boolean => {
  // A recorded failed attempt outranks the status. The status can be moved by a
  // path that does not clear `last_error` — an inbound webhook, or a sync that
  // reconciled the row — and suppressing on status alone hides a live failure on
  // a registration that now reads active.
  if ((input.retryCount ?? 0) >= RECORDED_A_FAILED_ATTEMPT) return false
  return (
    (!!input.vaspUserStatus && SUCCEEDED_STATUSES.includes(input.vaspUserStatus)) ||
    (!!input.vaspListingStatus && LISTING_SUCCEEDED_STATUSES.includes(input.vaspListingStatus))
  )
}

/**
 * Builds the drawer's failure banner, or `null` when there is nothing to show.
 * Every suppression rule lives here so no drawer template carries a status
 * check, and the five surfaces cannot drift apart.
 */
export function vaspFailureView(input: VaspFailureInput): VaspFailureView | null {
  const sentence = input.lastError?.trim()
  if (!sentence) return null
  if (hasSucceeded(input)) return null

  const source = input.lastErrorSource
  return {
    title: (source && VASP_FAILURE_TITLE[source]) || FALLBACK_TITLE,
    message: stripSourcePrefix(sentence),
    meta: buildMeta(input.retryCount, input.lastSyncedAt || input.lastAttemptAt || ''),
  }
}

/**
 * Wraps the message from a mutation that just failed. Outranks the persisted
 * banner: admin-service has recorded the same failure, but the refetch has not
 * landed yet — and for a call that failed before the row existed, never will.
 */
export function transientFailureView(message: string): VaspFailureView {
  return { title: TRANSIENT_FAILURE_TITLE, message: stripSourcePrefix(message), meta: '' }
}
